import {Component, OnInit, ViewChild} from '@angular/core';
import {DataService} from "../../services/data.service";
import {FrontEndService} from "../../services/front-end.service";
import {UserManagementService} from "../../services/user-management.service";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {LogService} from "../../services/log.service";
import {DashboardService} from "../../services/dashboard.service";
import {StateService} from '@uirouter/angular';
import {LineChartComponent, PieChartComponent} from "@swimlane/ngx-charts";
import * as moment from "moment";
import {Select2OptionData} from "ng2-select2";

@Component({
    selector: 'app-workspace-dashboard',
    templateUrl: './workspace-dashboard.component.html',
    styleUrls: ['./workspace-dashboard.component.scss']
})
export class WorkspaceDashboardComponent implements OnInit {

    @ViewChild('pieChart') pieChart: PieChartComponent;
    @ViewChild('seriesChart') lineChart: LineChartComponent;

    loading = false;
    trendLineLoading = false;
    reviewedCaseLoading = false;
    ftrLoading = false;

    // Common Options
    gradient = false;
    showLegend = true;
    view: any[] = [400, 400];
    colorScheme = {
        domain: ['#AAAAAA', '#5AA454', '#A10A28', '#C7B42C']
    };

    // Vertical bar chart
    showXAxis = true;
    showYAxis = true;
    showXAxisLabel = true;
    xAxisLabel = 'Date';
    showYAxisLabel = true;
    yAxisLabel = 'QC Passed';

    // Pie Chart
    doughnut = false;
    explodeSlices = false;
    showLabels = true;

    users = [];
    selectedUsers = [];
    dropdownSettings = {};

    pieChartResults = [];
    pieChartData = {};
    totalCases = {};

    reviewedCases = [];
    ftr = [];

    lineChartResults = [];
    lineChartData = {};

    startDate = "";
    startDatePicked = [];
    endDate = "";
    endDatePicked = [];

    aggregateReviewedCases = 0;

    dateType = "delivery_date";
    dateTypes: Array<Select2OptionData> = [
        {
            id: 'delivery_date',
            text: 'Delivery Date'
        },
        {
            id: 'qced_date',
            text: 'QCED Date'
        },
        {
            id: 'submission_date',
            text: 'Submission Date'
        },
    ];
    dateTypeOptions = {
        placeholder: 'Please select date type'
    };

    constructor(
        public dataService: DataService,
        public front_end_service: FrontEndService,
        private dashboardService: DashboardService,
        public modalService: NgbModal,
        private stateService: StateService,
        private userManagementService: UserManagementService,
        private logService: LogService
    ) {
        if (!this.dataService.workspaceEnabled) {
            this.stateService.go("Forbidden");
        }
    }

    ngOnInit() {
        this.endDate = moment().format("YYYY-MM-DD");
        this.startDate = moment().subtract(7, "days").format("YYYY-MM-DD");

        this.dropdownSettings = {
            singleSelection: false,
            idField: 'id',
            textField: 'text',
            selectAllText: 'Select All',
            unSelectAllText: 'UnSelect All',
            itemsShowLimit: 1,
            allowSearchFilter: true
        };
        this.getUsers();
    }

    getUsers() {
        this.loading = true;
        this.dashboardService.getTaskUsers((userResponse) => {
            this.loading = false;
            if (userResponse.status) {
                this.users = [{
                    "id": 0,
                    "text": "All Users"
                }];
                userResponse.data.forEach((user) => {
                    const userObject = {
                        "id": user.id,
                        "text": user.username
                    };
                    this.users.push(userObject);
                });
                this.selectedUsers = [{
                    "id": 0,
                    "text": "All Users"
                }];

                this.getPieChart();
                this.getTrendLine();
                this.getReviewedCases();
                this.getFTR();
            }
        });
    }

    userSelected(event) {
        // Handle user selected here
    }

    getPieChart() {
        this.loading = true;
        const data = {
            "start_date": this.startDate,
            "end_date": this.endDate,
            "status": "QC Passed",
            "date_field": this.dateType
        };
        this.pieChartResults = [];

        this.dashboardService.getPieChart(data, (response) => {
            if (response.status) {
                this.pieChartResults = response.data;
                this.setPieChartData();
            }
            this.loading = false;
        });
    }

    setPieChartData() {
        this.pieChartData = {};
        const users = Object.keys(this.pieChartResults);
        for (const index in users) {
            const user = users[index];

            let totalCases = 0;
            (this.pieChartResults[user] as Array<{}>).forEach((userData) => {
                if (userData.hasOwnProperty('status') && userData.hasOwnProperty('count')) {
                    userData['name'] = userData['status'];
                    userData['value'] = userData['count'];
                    totalCases += userData['count'];
                    delete userData['status'];
                    delete userData['count'];
                }
            });
            this.totalCases[user] = totalCases;
        }
    }

    getTrendLine() {
        const data = {
            "start_date": this.startDate,
            "end_date": this.endDate,
            "status": "QC Passed",
            "date_field": this.dateType
        };
        this.trendLineLoading = true;
        this.dashboardService.getTrendLine(data, (response) => {
            if (response.status) {
                this.lineChartResults = response.data;
                this.setTrendLineChart();
            }
            this.trendLineLoading = false;
        });
    }

    setTrendLineChart() {
        this.lineChartData = {};
        const aggregate = [];
        const users = Object.keys(this.lineChartResults);
        for (const index in users) {
            const user = users[index];
            const series = [];
            (this.lineChartResults[user] as Array<{}>).forEach(userData => {
                if (userData.hasOwnProperty('data') && userData.hasOwnProperty('count')) {
                    series.push({
                        "name": userData["data"],
                        "value": userData["count"]
                    })
                }
            });
            const lineSeries = {
                "name": user,
                "series": series
            };
            this.lineChartData[user] = [lineSeries];
            aggregate.push(lineSeries);
        }
    }

    getReviewedCases() {
        this.reviewedCaseLoading = true;
        const data = {
            "start_date": this.startDate,
            "end_date": this.endDate,
            "status": "QC Passed",
            "date_field": this.dateType
        };
        this.dashboardService.getReviewedCases(data, (response) => {
            if (response.status) {
                this.reviewedCases = response.data;
                this.aggregateReviewedCases = 0;
            }
            this.reviewedCaseLoading = false;
        });
    }

    getFTR() {
        this.ftrLoading = true;
        const data = {
            "start_date": this.startDate,
            "end_date": this.endDate,
            "status": "QC Passed",
            "date_field": this.dateType
        };
        this.dashboardService.getFTR(data, (response) => {
            if (response.status) {
                this.ftr = response.data;
            }
            this.ftrLoading = false;
        });
    }

    onSelect(event) {
        this.logService.printLog(event);
    }

    singleDateSelection(event, date) {
        // Empty function needed to enable day selection
    }

    checkDateSelection(type) {
        if (type === "start") {
            if (this.startDatePicked[0]) {
                this.startDate = moment(this.startDatePicked[0]._d).format("YYYY-MM-DD");
            } else {
                this.startDate = null;
            }
        } else {
            if (this.endDatePicked[0]) {
                this.endDate = moment(this.endDatePicked[0]._d).format("YYYY-MM-DD");
            } else {
                this.endDate = null;
            }
        }
        if (this.startDate !== null && this.endDate !== null) {
            this.getPieChart();
            this.getTrendLine();
            this.getReviewedCases();
            this.getFTR();
        }
    }

    dateTypeChanged(event) {
        this.dateType = event.value;
        this.getPieChart();
        this.getTrendLine();
        this.getReviewedCases();
        this.getFTR();
    }
}
